import React, { memo, useMemo, useRef, useState } from 'react';

import { Center, Text, Tabs, TabList, Tab } from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';
import { washhubSelector } from 'store/slices/washhub/washhub.selectors';

import { fetchEmployees } from 'api/employees';
import { useQuery } from 'react-query';
import { useWashHubListener } from 'hooks';

import {
  EditEmployeeModal,
  EditEmployeeModalRef,
} from 'components/modules/EditEmployeeModal';
import { DataTable } from 'components/modules/DataTable';
import { UserStatusEnum } from 'types/user';

import { buildEmployeeColumns } from './employeesColumns';

export type EmployeesTableProps = {
  /** First element of the react-query key for this screen. */
  queryKeyPrefix: string;
  /** washHub id to scope the fetch to, or undefined for the super-admin list
   *  (which spans all wash hubs). */
  washHubId: string | undefined;
  /** Extra fetchEmployees params that differ between screens (role, and
   *  whether the query is enabled on a valid wash hub). */
  role?: string;
  enabled?: boolean;
  /** WashHubAdmins resets to page 1 on wash hub change; SuperAdmins (which
   *  spans every wash hub) does not. */
  resetPageOnWashHubChange?: boolean;
};

const getStatusParams = (tab: 'active' | 'inactive' | 'all') => {
  if (tab === 'active') {
    return { status: UserStatusEnum.ACTIVE as any };
  }
  if (tab === 'inactive') {
    return {
      status: [
        UserStatusEnum.SUSPENDED,
        UserStatusEnum.DISABLED,
        UserStatusEnum.DELETED,
      ] as any,
    };
  }
  return {
    status: [
      UserStatusEnum.SUSPENDED,
      UserStatusEnum.DISABLED,
      UserStatusEnum.DELETED,
      UserStatusEnum.ACTIVE,
    ] as any,
  };
};

const _selected = {
  backgroundColor: 'primary.900',
  color: 'support.500',
} as any;
const _focus = { border: 'none', backgroundColor: 'primary.500' } as any;

/**
 * Shared body of SuperAdmins and WashHubAdmins — the tabs, query, table and
 * EditEmployeeModal wiring were copy-pasted between the two screens and
 * differed only in the role filter and wash-hub scoping, which are props
 * here (same consolidation shape as Pickups/PickupsTable).
 */
export const EmployeesTable = memo<EmployeesTableProps>((props) => {
  const {
    queryKeyPrefix,
    washHubId,
    role,
    enabled = true,
    resetPageOnWashHubChange = false,
  } = props;

  const editEmployeeModalRef = useRef<EditEmployeeModalRef>(null);

  const [page, setPage] = useState<number>(1);
  const [statusTab, setStatusTab] = useState<'active' | 'inactive' | 'all'>(
    'all',
  );

  useWashHubListener(() => {
    if (resetPageOnWashHubChange) {
      setPage(1);
    }
  });

  const queryKey = [queryKeyPrefix, page, statusTab];
  const statusParams = getStatusParams(statusTab);

  const queryFn = () =>
    fetchEmployees({
      page,
      limit: 10,
      washHub: washHubId,
      ...(role ? { role } : {}),
      ...statusParams,
    } as any);

  const options = {
    enabled,
    keepPreviousData: true,
  };

  const { data, isFetching, isLoading: _isLoading } = useQuery(
    queryKey,
    queryFn,
    options,
  );

  const isLoading = _isLoading || isFetching;

  const employees = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const washHubs = useAppSelector(washhubSelector);

  const getWashHubByID = (washID?: string) => {
    let washHubById = '';

    if (!Boolean(washID)) return (washHubById = ' - ');

    const washHub = washHubs.find((wash) => wash?._id === washID);
    washHubById = washHub?.name || ' - ';
    return washHubById;
  };

  const columns = useMemo(
    () => buildEmployeeColumns(getWashHubByID),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [washHubs],
  );

  return (
    <>
      <EditEmployeeModal ref={editEmployeeModalRef} />
      <Tabs
        onChange={(index) => {
          setPage(1);
          setStatusTab(
            index === 0 ? 'active' : index === 1 ? 'inactive' : 'all',
          );
        }}
        variant="enclosed"
        size="lg"
        index={statusTab === 'active' ? 0 : statusTab === 'inactive' ? 1 : 2}>
        <TabList
          color="support.500"
          _selected={_selected}
          _focus={_focus}
          w="100%"
          mb={4}>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Active
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Inactive
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            All
          </Tab>
        </TabList>
      </Tabs>
      <DataTable
        minW="1010px"
        data={employees}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onRowClick={(employee) =>
          editEmployeeModalRef?.current?.onOpen?.(employee)
        }
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No Employees available</Text>
          </Center>
        )}
      />
    </>
  );
});
